import { logger } from '@shared/lib/logger'
import { useCustomerProfile, useIntents, type Country, type CountryCode, type EmailResult, type PhoneNumberResult } from '@vp/contact-channels'
import { useIdentity, useStyles } from '@vp/ubik-context'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { stylesheet } from '../components/steps/styles.scss'
import { useFormData } from '../context/form-data-context'
import { usePageFlow } from '../context/page-flow-context'
import { useOrders } from './use-orders'
import { useProjects } from './use-projects'

export interface ContactDetailsFields {
  intentId: string
  firstName: string
  lastName: string
  email: EmailResult
  country?: Country
  phone: PhoneNumberResult
  optInRecording: boolean
}

export const useContactDetails = () => {
  const { t } = useTranslation()
  const { channel, setNextConfig, registerOnNext, goNext, setNextLoading, channelDisabled, channelStatusAlertKey } = usePageFlow()
  const { formData, updateFormData } = useFormData()
  const { signIn, signOut } = useIdentity()
  const { isLoading: isOrdersLoading, prefetch: prefetchOrders } = useOrders()
  const { isLoading: isProjectsLoading, prefetch: prefetchProjects } = useProjects()
  const isOrdersLoadingRef = useRef(isOrdersLoading)
  const isProjectsLoadingRef = useRef(isProjectsLoading)
  const pendingNextRef = useRef(false)
  useStyles(stylesheet)

  const { data: customerProfile, isLoading } = useCustomerProfile()
  const intents = useIntents()

  const profileFirstName = customerProfile?.profile?.firstName ?? ''
  const profileLastName = customerProfile?.profile?.lastName ?? ''
  const profileEmail = customerProfile?.profile?.email ?? ''
  const isSignedIn = customerProfile?.isSignedIn ?? false
  const callbackCountry = formData.countryCode && formData.country ? { code: formData.countryCode as CountryCode, name: formData.country } : undefined

  const hasPrefilledRef = useRef(false)

  const {
    control,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<ContactDetailsFields>({
    mode: 'onChange',
    defaultValues: {
      intentId: formData.intentId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: { email: formData.email, isValid: !!formData.email },
      country: formData.channel === 'callback' ? callbackCountry : undefined,
      phone: formData.channel === 'callback' ? { phoneNumber: formData.phone, isValid: true } : { phoneNumber: '', isValid: false, prefix: '+1' },
      optInRecording: formData.channel === 'callback' ? formData.optInRecording : false,
    },
  })

  useEffect(() => {
    if (isLoading) return
    if (hasPrefilledRef.current) return

    const didPrefillFirstName = !formData.firstName && Boolean(profileFirstName)
    const didPrefillLastName = !formData.lastName && Boolean(profileLastName)
    const didPrefillEmail = !formData.email && Boolean(profileEmail)

    if (!formData.firstName && profileFirstName) setValue('firstName', profileFirstName, { shouldValidate: true })
    if (!formData.lastName && profileLastName) setValue('lastName', profileLastName, { shouldValidate: true })
    if (!formData.email && profileEmail) setValue('email', { email: profileEmail, isValid: true }, { shouldValidate: true })

    if (didPrefillFirstName || didPrefillLastName || didPrefillEmail) {
      hasPrefilledRef.current = true
      logger.info('Contact details prefilled from customer profile', {
        action: 'contact_details_prefilled',
        component: 'useContactDetails',
        channel,
        prefilledFirstName: didPrefillFirstName,
        prefilledLastName: didPrefillLastName,
        prefilledEmail: didPrefillEmail,
      })
    }
  }, [channel, isLoading, profileFirstName, profileLastName, profileEmail, setValue])

  useEffect(() => {
    isOrdersLoadingRef.current = isOrdersLoading
  }, [isOrdersLoading])

  useEffect(() => {
    isProjectsLoadingRef.current = isProjectsLoading
  }, [isProjectsLoading])

  useEffect(() => {
    if (!pendingNextRef.current) return
    const relevantLoading =
      (formData.intentId === 'help_with_my_order' && isOrdersLoading) || (formData.intentId === 'help_with_my_design' && isProjectsLoading)
    if (relevantLoading) return
    pendingNextRef.current = false
    setNextLoading(false)
    goNext()
  }, [isOrdersLoading, isProjectsLoading, formData.intentId, goNext, setNextLoading])

  useEffect(() => {
    const subscription = watch((values) => {
      const emailResult = values.email
      const countryResult = values.country
      const phoneResult = values.phone

      if (values.intentId === 'help_with_my_order') {
        void prefetchOrders()
      }

      if (values.intentId === 'help_with_my_design') {
        void prefetchProjects()
      }

      updateFormData({
        ...values,
        email: emailResult?.email ?? '',
        countryCode: countryResult?.code ?? '',
        country: countryResult?.name ?? '',
        phone: phoneResult?.phoneNumber ?? '',
      } as Parameters<typeof updateFormData>[0])
    })
    return () => subscription.unsubscribe()
  }, [watch, updateFormData, prefetchOrders, prefetchProjects])

  useEffect(() => {
    registerOnNext(() => {
      const isOrderPending = formData.intentId === 'help_with_my_order' && isOrdersLoadingRef.current
      const isProjectPending = formData.intentId === 'help_with_my_design' && isProjectsLoadingRef.current
      if (isOrderPending || isProjectPending) {
        setNextLoading(true)
        pendingNextRef.current = true
        return
      }
      goNext()
    })
    return () => {
      pendingNextRef.current = false
    }
  }, [registerOnNext, goNext, formData.intentId, setNextLoading])

  useEffect(() => {
    setNextConfig({ disabled: !isValid || channelDisabled })

    logger.info('Contact details next action state updated', {
      action: 'contact_details_next_state_updated',
      component: 'useContactDetails',
      channel,
      isValid,
      channelDisabled,
      hasChannelStatusAlert: Boolean(channelStatusAlertKey),
    })
  }, [channel, channelDisabled, channelStatusAlertKey, isValid, setNextConfig])

  const displayName = isSignedIn ? profileFirstName || profileLastName || profileEmail : ''
  const title =
    isSignedIn && displayName
      ? t('contact_us.page.steps.details.title_logged_in', { firstName: displayName })
      : t('contact_us.page.steps.details.title_logged_out')

  return {
    control,
    channel,
    intents,
    isLoading,
    isSignedIn,
    profileEmail,
    displayName,
    title,
    formData,
    signIn,
    signOut,
    channelStatusAlertKey,
  }
}
